import Cart from "./cart-utils.js";
import Product from "./product-utils.js";

class ShippingProtection extends HTMLElement {
  constructor() {
    super();

    this.attachShadow({ mode: "open" });
    this.previousItemCount = 0;
  }

  async connectedCallback() {
    [{ value: this.cart }, { value: this.protectionProduct }] = await Promise.allSettled([
      Cart.get(),
      Product.get("shipping-protection"),
    ]);

    this.isProtected = this.cart.items.some(
      (item) => item.product_id === this.protectionProduct?.id
    );
    this.protectionPrice = this.getProtectionPrice();
    this.previousProtectionPrice = this.protectionPrice;

    if (this.protectionProduct) {
      this.render();
    }

    this.shadowInput = this.shadowRoot.querySelector("input");
    this.shadowPriceSlot = this.shadowRoot.querySelector('slot[name="price"]');

    this.lightPriceElements = this.shadowPriceSlot?.assignedElements();

    this.lightPriceElements?.forEach((element) => {
      element.innerText = "$" + this.protectionPrice;
    });

    this.addEventListeners();
  }

  render() {
    // console.log("geolocation", geolocation);

    this.shadowRoot.innerHTML = `
      <style>
        .container {
          display: flex;
          position: relative;
          border: 1px solid #ccc;
        }
        label {
          padding: 10px;
          width: 100%;
          display: flex;
          align-items: center;
          position: relative;
          cursor: pointer;
        }
        input[type="checkbox"] {
          appearance: none;
          background-color: #fff;
          margin: 0 6px 0 0;
          font: inherit;
          color: #212121;
          width: 1.2em;
          height: 1.2em;
          border: 0.15em solid #212121;
          border-radius: 0.2em;
          position: relative;
        }
        input[type="checkbox"]::before {
          content: "";
          position: absolute;
          width: 4px;
          height: 9px;
          left: 3px;
          top: -1px;
          border: solid #fff;
          border-width: 0 3px 3px 0;
          -webkit-transform: rotate(45deg);
          -ms-transform: rotate(45deg);
          transform: rotate(45deg);
          display: none;
        }
        input[type="checkbox"]:checked { background-color: #212121; }
        input[type="checkbox"]:checked::before { display: block; }
      </style>
      ${
        isShippingProtectionAvailable
          ? `<div class='container'>
         <label>
           <input type='checkbox' ${this.isProtected ? "checked" : ""}>
           ${window.cartStrings.shippingProtection}
           <slot name='price'></slot>
         </label>
         <slot name='tooltip'></slot>
       </div>`
          : ""
      }
    `;
  }

  addEventListeners() {
    function sleep(ms) {
      return new Promise((resolve) => setTimeout(resolve, ms));
    }

    this.shadowInput?.addEventListener("change", async (e) => {
      this.shadowInput.disabled = true;

      if (e.target.checked) {
        await sleep(1000);
        await this.addProtectionProduct();
      } else {
        await sleep(1000);
        await this.removeProtectionProduct();
      }

      document.dispatchEvent(
        new CustomEvent("theme:cartchanged", {
          bubbles: true,
          cancelable: false,
          detail: { preventVariantUpdate: true },
        })
      );
    });

    document.addEventListener("theme:cartchanged", async (e) => {
      this.previousItemCount = this.cart?.item_count || 0;
      this.previousProtectionPrice = this.protectionPrice;
      this.cart = await Cart.get();
      this.protectionPrice = this.getProtectionPrice();
      this.cart = await Cart.get();

      this.lightPriceElements?.forEach((element) => {
        element.innerText = "$" + this.protectionPrice;
      });

      this.isProtected = this.cart.items.some(
        (item) => item.product_id === this.protectionProduct?.id
      );

      if (!e.detail?.preventVariantUpdate) {
        this.shadowInput.checked = this.isProtected;

        if (this.isProtected) {
          if (this.previousProtectionPrice !== this.protectionPrice) {
            await this.changeProtectionVariant();
          }
        }
      }

      if (this.isProtected && this.cart.item_count === 1) {
        this.removeProtectionProduct();

        document.dispatchEvent(
          new CustomEvent("theme:cartchanged", {
            bubbles: true,
            cancelable: false,
            detail: { preventVariantUpdate: true },
          })
        );
      }

      if (this.shadowInput) {
        this.shadowInput.disabled = false;
      }
    });
  }

  async addProtectionProduct(options) {
    this.cart = await Cart.get();
    if (this.isProtected) return;

    const protectionVariant = this.protectionProduct?.variants[0];

    await Cart.add(protectionVariant.id, options);

    this.cart = await Cart.get();
    this.closest("side-cart").reRenderCart(this.cart);

    this.isProtected = true;
  }

  async removeProtectionProduct() {
    [{ value: this.cart }, { value: this.protectionProduct }] = await Promise.allSettled([
      Cart.get(),
      Product.get("shipping-protection"),
    ]);

    if (!this.isProtected || !this.protectionProduct) return;

    const protectionItem = this.cart.items.find(
      (item) => item.product_id === this.protectionProduct?.id
    );

    if (protectionItem) {
      await Cart.update({ [protectionItem.variant_id]: 0 });
    }

    this.cart = await Cart.get();
    this.closest("side-cart").reRenderCart(this.cart);

    this.isProtected = false;
  }

  async changeProtectionVariant() {
    this.shadowInput.disabled = true;

    await this.removeProtectionProduct();
    await this.addProtectionProduct();

    document.dispatchEvent(
      new CustomEvent("theme:cartchanged", {
        bubbles: true,
        cancelable: false,
        detail: { preventVariantUpdate: true },
      })
    );
  }

  autoProtect() {
    if (this.previousItemCount === 0 && this.cart?.item_count > 0 && !this.shadowInput.checked) {
      this.shadowInput.click();
    }
  }

  getProtectionPrice() {
    let baselinePrice = this.protectionProduct?.variants[0].price;
    let protectionPrice = baselinePrice;

    return protectionPrice / 100;
  }
}

customElements.define("shipping-protection", ShippingProtection);
